import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  Search, Megaphone, CheckCircle, AlertTriangle, Trash2, Link as LinkIcon, Plug,
} from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardHeader, CardBody, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { useToast } from '@/hooks/useToast';
import { useModuleAccess } from '@/hooks/useAccountModules';
import { TrackingPixelsCard } from '@/pages/admin/TrackingPixelsCard';
import { GoogleBusinessIntegrationCard } from '@/pages/admin/GoogleBusinessIntegrationCard';
import { ZernioIntegrationCard } from '@/pages/admin/ZernioIntegrationCard';
import { cn } from '@/lib/utils';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import {
  listMarketingIntegrations,
  generateWindsorConnectLink,
  checkWindsorConnection,
  disconnectWindsorDatasource,
  PROVIDER_LABELS,
} from '@/services/marketingIntegrations.service';
import type {
  MarketingProvider, MarketingIntegrationRow, WindsorDatasource, WindsorDatasourceStatus,
} from '@/services/marketingIntegrations.service';

function describeApiError(e: any): string {
  return e?.response?.data?.error?.message ?? e?.message ?? 'Request failed';
}

type MarketingGroup = 'google' | 'meta';

/**
 * "Ads Connector" — one guided flow, no mention anywhere in the UI that
 * Windsor.ai is the underlying vendor. ONE connect link puts Google
 * (Analytics GA4 + Ads + Search Console) and Meta Ads on the same panel:
 * the clinic signs in to each vendor on that one page and clicks Finish
 * once. The backend's group "all" targets whichever datasources aren't
 * connected yet, so the same button also finishes a partial setup.
 */
const GROUP_CONFIG: Record<MarketingGroup, {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  tone: 'blue' | 'sky';
  datasources: WindsorDatasource[];
  covers: string;
}> = {
  google: {
    label: 'Google',
    icon: Search,
    tone: 'blue',
    datasources: ['googleAds', 'ga4', 'searchConsole'],
    covers: 'Google Analytics (GA4) · Google Ads (AdWords) · Search Console',
  },
  meta: {
    label: 'Meta',
    icon: Megaphone,
    tone: 'sky',
    datasources: ['metaAds'],
    covers: 'Meta Ads (Facebook & Instagram)',
  },
};

type GroupStatus = 'connected' | 'pending' | 'not_connected';

function groupStatus(
  datasources: Partial<Record<WindsorDatasource, WindsorDatasourceStatus>>,
  group: MarketingGroup,
): { status: GroupStatus; accountName: string | null } {
  const statuses = GROUP_CONFIG[group].datasources.map((d) => datasources[d]?.status ?? 'not_connected');
  const accountName = GROUP_CONFIG[group].datasources.map((d) => datasources[d]?.accountName).find(Boolean) ?? null;
  if (statuses.some((s) => s === 'connected')) return { status: 'connected', accountName };
  if (statuses.some((s) => s === 'pending')) return { status: 'pending', accountName };
  return { status: 'not_connected', accountName };
}

function StatusPill({ status }: { status: GroupStatus }) {
  if (status === 'connected') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-50 text-emerald-700">
        <CheckCircle className="h-3 w-3" /> Connected
      </span>
    );
  }
  if (status === 'pending') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-50 text-amber-700">
        <AlertTriangle className="h-3 w-3" /> Waiting to finish…
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-500">
      <Plug className="h-3 w-3" /> Not connected
    </span>
  );
}

/**
 * Locked (plan 2026-10-10-001 U7: R8, R18): each group's connection state
 * shows, with no Connect, Start over, Check connection (it resolves pending
 * datasources server-side, so it writes) or Disconnect, and no sign-in
 * instructions. Nothing says why.
 */
function AdsConnectorCard({ row }: { row: MarketingIntegrationRow | undefined }) {
  const locked = useAdminPageLevel() === 'locked';
  const toast = useToast();
  const qc = useQueryClient();
  const datasources = row?.datasources ?? ({} as Record<WindsorDatasource, WindsorDatasourceStatus>);

  const google = groupStatus(datasources, 'google');
  const meta = groupStatus(datasources, 'meta');

  // True while we auto-poll after opening the combined connect tab.
  const [polling, setPolling] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<MarketingGroup | null>(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin', 'marketing-integrations'] });

  const connectMut = useMutation({
    // One link for everything still missing (backend group "all") — Google
    // and Meta live on the same Windsor panel behind a single Finish click.
    mutationFn: (force: boolean) => generateWindsorConnectLink('all', force),
    onSuccess: (res) => {
      window.open(res.url, '_blank', 'noopener,noreferrer');
      toast.success('In the new tab: connect Google AND Meta (each has its own sign-in button), then click "Finish" — we\'ll pick it up automatically.');
      setPolling(true);
      invalidate();
    },
    onError: (e: any) => toast.error(describeApiError(e)),
  });

  const checkMut = useMutation({
    mutationFn: () => checkWindsorConnection(),
    onSuccess: (res) => {
      const names = (['google', 'meta'] as MarketingGroup[])
        .filter((g) => res.resolved.some((d) => GROUP_CONFIG[g].datasources.includes(d)))
        .map((g) => GROUP_CONFIG[g].label);
      if (names.length) toast.success(`${names.join(' and ')} connected.`);
      invalidate();
    },
    onError: (e: any) => toast.error(describeApiError(e)),
  });

  const disconnectMut = useMutation({
    mutationFn: (group: MarketingGroup) =>
      Promise.all(GROUP_CONFIG[group].datasources.map((d) => disconnectWindsorDatasource(d))),
    onSuccess: (_res, group) => {
      toast.success(`${GROUP_CONFIG[group].label} disconnected`);
      invalidate();
      setConfirmDelete(null);
    },
    onError: (e: any) => toast.error(describeApiError(e)),
  });

  // Auto-poll for up to 2 minutes after opening a connect link so the clinic
  // doesn't have to remember to click "Check connection" themselves — the
  // windsor:poll-connections scheduled command is the backstop beyond that.
  useEffect(() => {
    if (!polling) return;
    const id = setInterval(() => checkMut.mutate(), 5000);
    const stop = setTimeout(() => setPolling(false), 2 * 60 * 1000);
    return () => { clearInterval(id); clearTimeout(stop); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [polling]);

  const allConnected = google.status === 'connected' && meta.status === 'connected';
  const anyPending = google.status === 'pending' || meta.status === 'pending';
  const startingFresh = google.status === 'not_connected' && meta.status === 'not_connected';
  const missingLabels = (['google', 'meta'] as MarketingGroup[])
    .filter((g) => (g === 'google' ? google : meta).status !== 'connected')
    .map((g) => GROUP_CONFIG[g].label);

  const steps: { group: MarketingGroup; state: { status: GroupStatus; accountName: string | null } }[] = [
    { group: 'google', state: google },
    { group: 'meta', state: meta },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Ads Connector</CardTitle>
      </CardHeader>
      <CardBody className="space-y-5">
        {locked ? (
          <p className="text-sm text-slate-500">
            Connects everything we report on: Google Analytics (GA4), Google Ads (AdWords),
            Google Search Console and Meta Ads.
          </p>
        ) : (
          <p className="text-sm text-slate-500">
            One guided setup connects everything we report on: Google Analytics (GA4), Google
            Ads (AdWords), Google Search Console and Meta Ads. One secure page opens with a
            connect button for Google (one consent covers all three Google products) and one
            for Meta — sign in to both, click "Finish" once, and we handle the rest.
          </p>
        )}

        {/* Step list — always visible so the clinic can see where they are. */}
        <div className="space-y-3">
 {steps.map(({ group, state }) => {
            const cfg = GROUP_CONFIG[group];
            const Icon = cfg.icon;
            const isActive = !allConnected && state.status !== 'connected';
            return (
              <div
                key={group}
                className={cn(
                  'rounded-lg border px-3 py-3',
                  state.status === 'connected'
                    ? 'border-emerald-200 bg-emerald-50/40'
                    : isActive
                      ? 'border-slate-300 bg-white'
                      : 'border-slate-200 bg-slate-50/60',
                )}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={cn(
                      'h-9 w-9 rounded-lg grid place-items-center shrink-0',
                      cfg.tone === 'blue' ? 'bg-blue-50 text-blue-600' : 'bg-sky-50 text-sky-600',
                    )}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-800">
                        {locked ? cfg.label : `Sign in with ${cfg.label}`}
                      </div>
                      <div className="text-xs text-slate-500 truncate">{cfg.covers}</div>
                      <div className="mt-1"><StatusPill status={state.status} /></div>
                    </div>
                  </div>
                  {state.status === 'connected' && (
                    <div className="flex items-center gap-2 shrink-0">
                      {state.accountName && (
                        <span className="hidden sm:inline text-xs text-slate-500">
                          as <span className="font-medium text-slate-700">{state.accountName}</span>
                        </span>
                      )}
                      {!locked && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setConfirmDelete(group)}
                          className="text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Primary action — one button drives the whole flow. */}
        {allConnected ? (
          <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800 flex items-center gap-2">
            <CheckCircle className="h-4 w-4 shrink-0" />
            All ads accounts are connected. Reports across Google and Meta are live.
          </div>
        ) : locked ? null : anyPending ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <Button variant="ghost" onClick={() => checkMut.mutate()} disabled={checkMut.isPending}>
                {checkMut.isPending ? <Spinner className="h-4 w-4 mr-1" /> : <CheckCircle className="h-4 w-4 mr-1" />}
                Check connection
              </Button>
              <Button
                variant="ghost"
                onClick={() => connectMut.mutate(true)}
                disabled={connectMut.isPending}
              >
                <LinkIcon className="h-4 w-4 mr-2" />
                Start over
              </Button>
            </div>
            <p className="text-xs text-slate-500">
              Didn't finish signing in, or the tab got closed? "Start over" opens a fresh sign-in link.
            </p>
          </div>
        ) : (
          <Button
            onClick={() => connectMut.mutate(false)}
            disabled={connectMut.isPending}
          >
            <LinkIcon className="h-4 w-4 mr-2" />
            {connectMut.isPending
              ? 'Opening…'
              : startingFresh
                ? 'Connect ads accounts (Google + Meta)'
                : `Connect remaining — ${missingLabels.join(' + ')}`}
          </Button>
        )}
      </CardBody>

      <ConfirmDialog
        isOpen={confirmDelete !== null}
        title={`Disconnect ${confirmDelete ? GROUP_CONFIG[confirmDelete].label : ''}?`}
        message="You can reconnect anytime."
        confirmLabel="Disconnect"
        variant="danger"
        onConfirm={() => (confirmDelete ? disconnectMut.mutateAsync(confirmDelete).then(() => undefined) : Promise.resolve())}
        onClose={() => setConfirmDelete(null)}
      />
    </Card>
  );
}

export default function MarketingIntegrationsAdmin() {
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const qc = useQueryClient();

  // Surface the OAuth-callback redirect outcome from the URL the provider
  // bounces us back to.
  useEffect(() => {
    const oauth = searchParams.get('oauth');
    const provider = searchParams.get('provider');
    const reason = searchParams.get('reason');
    if (!oauth) return;
    if (oauth === 'connected' && provider) {
      toast.success(`${PROVIDER_LABELS[provider as MarketingProvider] ?? provider} connected. Pick an account to finish.`);
      qc.invalidateQueries({ queryKey: ['admin', 'marketing-integrations'] });
    } else if (oauth === 'error') {
      toast.error(`Connection failed${reason ? `: ${reason}` : ''}`);
    }
    // Strip the params so a refresh doesn't re-toast.
    searchParams.delete('oauth');
    searchParams.delete('provider');
    searchParams.delete('reason');
    setSearchParams(searchParams, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'marketing-integrations'],
    queryFn: listMarketingIntegrations,
  });

  const windsorRow = (data ?? []).find((r) => r.provider === 'windsor_ai');
  const hasZernio = useModuleAccess('zernio').explicit;
  const hasGoogleBusiness = useModuleAccess('google business').explicit;

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title="Marketing Integrations"
        subtitle="One guided connector for all your ads and analytics accounts. We handle the sign-in and permissions — no separate accounts to manage."
      />
      {isLoading ? (
        <div className="flex items-center justify-center h-64"><Spinner /></div>
      ) : (
        <div className="space-y-5">
          <AdsConnectorCard row={windsorRow} />
          <TrackingPixelsCard rows={data ?? []} />
          {/* Above Zernio deliberately: Google Business is what a practice
              recognises and comes looking for; Zernio is the plumbing under it.
              Separate gates since 2026-09 — an account can hold one without the
              other. Google Business still needs the key, which is why the card
              says so when it is missing rather than hiding.

              The Zernio card therefore shows for EITHER module: Google Business
              tells the admin to add the key "below", and on a Google-only
              account that card would otherwise not be rendered, leaving the
              instruction pointing at nothing and the account unfixable from
              this screen. */}
          {hasGoogleBusiness && <GoogleBusinessIntegrationCard />}
          {(hasZernio || hasGoogleBusiness) && <ZernioIntegrationCard />}
        </div>
      )}
    </div>
  );
}
